import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Music, LogOut, Loader2, Music2, Download, UserCircle } from 'lucide-react';
import AccountModal from '../components/AccountModal';
import StatusBadge from '../components/dashboard/StatusBadge';
import SongSwitcher from '../components/dashboard/SongSwitcher';
import VersionList from '../components/dashboard/VersionList';
import PurchaseBar, { MultiBuyPromo } from '../components/dashboard/PurchaseBar';
import AfterPurchaseTools from '../components/dashboard/AfterPurchaseTools';
import MarketingPanel from '../components/dashboard/MarketingPanel';
import LaunchPad from '../components/dashboard/LaunchPad';
import SubscriptionCard from '../components/dashboard/SubscriptionCard';
import ApprovalInbox from '../components/dashboard/ApprovalInbox';
import ReleasePanel from '../components/dashboard/ReleasePanel';
import PostCalendar from '../components/dashboard/PostCalendar';
import ReceiptsPanel from '../components/dashboard/ReceiptsPanel';
import RichText from '../components/RichText';
import { useAuth } from '../context/AuthContext';
import { API, errText } from '../api';
import { downloadHref as dlHref } from '../lib/song';
import { STEPS } from '../mock';

const STATUS_IDX = { new: 0, studio: 1, ready: 2, purchased: 3 };
const codeNum = (r) => Number((r.code || '').replace(/\D/g, '')) || 0;
const previewNote = (s) => (s ? `Previews play the first ${s >= 60 ? `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}` : `${s} seconds`} of each version.` : 'Each version plays in full before you buy.');

export default function Dashboard() {
  const nav = useNavigate();
  const { user, loading, logout } = useAuth();
  const [reqs, setReqs] = useState([]);
  const [mkList, setMkList] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [selected, setSelected] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [preselect] = useState(null);
  const [plans, setPlans] = useState(null);
  const [posts, setPosts] = useState([]);
  const [subs, setSubs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);
  const [showAccount, setShowAccount] = useState(false);
  const [params, setParams] = useSearchParams();
  const [focus, setFocus] = useState(params.get('song'));

  const refresh = useCallback(() => API.listRequests().then(setReqs).catch((e) => console.warn('refresh failed', e)), []);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav('/signin'); return undefined; }
    if (user.role === 'studio') { nav('/studio'); return undefined; }
    Promise.all([API.listRequests(), API.listMarketing(), API.receipts(), API.pricing(), API.plans()])
      .then(([r, m, rc, pr, pl]) => { setReqs(r); setMkList(m); setReceipts(rc); setPricing(pr); setPlans(pl); })
      .catch((e) => toast.error(errText(e, 'Could not load your dashboard.')))
      .finally(() => setReady(true));
    // keep in sync with changes Perry makes in the studio (preview length, versions, status)
    const timer = setInterval(refresh, 20000);
    const onVisible = () => document.visibilityState === 'visible' && refresh();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', refresh);
    return () => { clearInterval(timer); document.removeEventListener('visibilitychange', onVisible); window.removeEventListener('focus', refresh); };
  }, [user, loading, nav, refresh]);

  if (loading || !ready) return <div className="min-h-screen bg-ink grid place-items-center"><Loader2 className="text-gold animate-spin" size={28} /></div>;

  const ordered = [...reqs].sort((a, b) => codeNum(b) - codeNum(a));
  const req = ordered.find((r) => r.code === focus) || ordered[0];
  const focusSong = (code) => { setFocus(code); setSelected([]); setParams(code ? { song: code } : {}, { replace: true }); };
  const replaceReq = (updated) => setReqs((prev) => prev.map((r) => (r.code === updated.code ? updated : r)));
  const owned = req?.purchased_versions || [];
  const purchasedVersion = req && req.versions?.find((v) => v.id === req.purchased_version);
  const downloadHref = dlHref(purchasedVersion);
  const hasVersions = req?.versions?.length > 0;
  const allOwned = hasVersions && req.versions.every((v) => owned.includes(v.id));
  const picked = selected.filter((id) => !owned.includes(id));

  const toggle = (id) => setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const feature = async (id) => {
    try { replaceReq(await API.setFeatured(req.code, id)); toast.success(`Version ${id} is now featured on your share page.`); }
    catch (e) { toast.error(errText(e, 'Could not update featured version.')); }
  };

  const buy = async () => {
    if (!picked.length || !req) return;
    setBusy(true);
    try {
      const { checkout_url } = await API.checkout({ kind: 'song', code: req.code, version_ids: picked });
      window.location.assign(checkout_url);
    } catch (e) { toast.error(errText(e, 'Could not start checkout.')); setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-ink grain">
      <div className="border-b hairline sticky top-0 z-30 bg-ink/85 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-6 h-[72px] flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <span className="grid place-items-center w-10 h-10 rounded-full border hairline"><Music size={16} className="text-gold" /></span>
            <span className="font-display text-lg text-cream">Brand <span className="italic text-gold-grad">Melody</span><span className="block eyebrow text-[8px] mt-0.5">BY P. FRAZY</span></span>
          </Link>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowAccount(true)} data-testid="account-btn" className="btn-ghost rounded-full px-4 py-2 text-sm flex items-center gap-2"><UserCircle size={15} /> <span className="hidden sm:inline">Account</span></button>
            <button onClick={() => { logout(); nav('/'); }} className="btn-ghost rounded-full px-4 py-2 text-sm flex items-center gap-2"><LogOut size={15} /> <span className="hidden sm:inline">Sign out</span></button>
          </div>
        </div>
      </div>
      {showAccount && <AccountModal user={user} onClose={() => setShowAccount(false)} />}

      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="eyebrow mb-3">Your dashboard</p>
            <h1 className="font-display text-5xl md:text-6xl text-cream leading-[0.95]">Welcome back, <span className="italic text-gold-grad">{user.owner_name || user.business_name}.</span></h1>
            <p className="mt-3 text-cream/60">{req ? (req.status === 'purchased' ? 'Your song is ready to download.' : 'You have a song waiting for you.') : 'Start your first song below.'}</p>
          </div>
          <button onClick={() => nav('/start')} className="btn-ghost rounded-full px-5 py-2.5 text-sm flex items-center gap-2"><Music2 size={15} className="text-gold" /> New song</button>
        </div>

        <SongSwitcher songs={ordered} activeCode={req?.code} onSelect={focusSong} />

        {!req ? (
          <div className="mt-12 panel rounded-3xl p-14 text-center">
            <span className="grid place-items-center w-16 h-16 rounded-2xl bg-gold/10 mx-auto mb-5"><Music2 className="text-gold" size={26} /></span>
            <h2 className="font-display text-3xl text-cream mb-2">No songs yet.</h2>
            <p className="text-cream/60 mb-6" data-testid="empty-state-copy">Tell Perry Frazier about your business and he&rsquo;ll write 2 to 3 Brand Melody versions for your business.</p>
            <button onClick={() => nav('/start')} className="btn-gold rounded-full px-7 py-3.5 inline-flex items-center gap-2">Get your song</button>
          </div>
        ) : (
          <div className={`${ordered.length > 1 ? 'mt-5' : 'mt-10'} grid lg:grid-cols-3 gap-6`}>
            <div className="lg:col-span-2 panel rounded-3xl p-7">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-4">
                  <span className="grid place-items-center w-14 h-14 rounded-2xl bg-gold/10 ring-1 ring-gold/20"><Music size={24} className="text-gold" /></span>
                  <div>
                    <h2 className="font-display text-3xl text-cream leading-tight">{req.business}</h2>
                    <p className="text-sm text-cream/50">{req.code} &middot; requested {req.requested}{req.delivered ? ` \u00b7 delivered ${req.delivered}` : ''}</p>
                  </div>
                </div>
                <StatusBadge status={req.status} />
              </div>

              <div className="grid grid-cols-4 gap-2 mt-8">
                {STEPS.map((s, i) => (
                  <div key={s}>
                    <div className="h-1 rounded-full mb-2 transition-colors duration-500" style={{ background: i <= STATUS_IDX[req.status] ? '#E0B755' : 'rgba(224,183,85,0.15)' }} />
                    <p className={`text-xs ${i === STATUS_IDX[req.status] ? 'text-cream font-semibold' : 'text-cream/40'}`}>{s}</p>
                  </div>
                ))}
              </div>

              {req.message && (
                <div className="mt-7 flex gap-3 rounded-2xl bg-panel2/50 border hairline p-4">
                  <span className="grid place-items-center w-9 h-9 rounded-full bg-gold/15 text-gold text-xs font-bold shrink-0">PF</span>
                  <div><p className="text-sm text-cream/80 font-semibold mb-1">P. Frazy</p><RichText text={req.message} className="text-sm text-cream/65 leading-relaxed" /></div>
                </div>
              )}

              {hasVersions && <MultiBuyPromo req={req} pricing={pricing} />}
              <VersionList req={req} picked={picked} onToggle={toggle} onFeature={feature} />

              {hasVersions && owned.length > 0 && (
                <div className="mt-7 flex items-center justify-between flex-wrap gap-4 pt-6 border-t hairline" data-testid="owned-summary">
                  <div>
                    <p className="font-display text-2xl text-cream">{owned.length === 1 ? 'Your song is yours.' : `${owned.length} versions are yours.`}</p>
                    <p className="eyebrow text-[9px] mt-1">Full tracks &middot; downloads &middot; license</p>
                  </div>
                  {downloadHref ? <a href={downloadHref} download data-testid="download-song-btn" className="btn-gold rounded-full px-7 py-3.5 flex items-center gap-2"><Download size={17} /> Download {owned.length > 1 ? `version ${req.purchased_version}` : 'your song'}</a>
                    : <span className="btn-ghost rounded-full px-7 py-3.5 flex items-center gap-2 opacity-70"><Download size={17} /> Download coming soon</span>}
                </div>
              )}
              {hasVersions && !allOwned && <PurchaseBar req={req} picked={picked} pricing={pricing} busy={busy} onBuy={buy} />}
              {hasVersions && <p className="mt-4 text-xs text-cream/35" data-testid="preview-note">{previewNote(req.preview_seconds)} Secure checkout by Stripe.</p>}
              {req.status === 'purchased' && <AfterPurchaseTools req={req} onUpdated={replaceReq} />}
              {req.status === 'purchased' && <LaunchPad req={req} plans={plans} />}
              {req.status === 'purchased' && <ReleasePanel req={req} />}
            </div>

            <div className="space-y-6">
              <ApprovalInbox onPosts={setPosts} />
              {(posts.length > 0 || subs.some((x) => x.status === 'active')) && <PostCalendar posts={posts} quota={plans?.tiers?.[subs.find((x) => x.status === 'active')?.tier]?.posts} />}
              <SubscriptionCard plans={plans} onLoaded={setSubs} />
              <MarketingPanel requestCode={req.code} items={mkList} onItems={setMkList} preselect={preselect} />
              <ReceiptsPanel receipts={receipts} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
